import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Landmark, Loader2, ReceiptText } from "lucide-react";
import { toast } from "sonner";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { OverlayShell } from "@/components/ui/overlay";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { format } from "@/lib/format";

export function CashDepositEvidenceModal({
	id,
	linked,
	onClose,
}: {
	id: number;
	linked: boolean;
	onClose: () => void;
}) {
	const queryClient = useQueryClient();
	const candidates = useQuery(
		orpc.financeiro.cash.depositCandidates.queryOptions({ input: { id } }),
	);
	const { mutate, isPending } = useMutation(
		orpc.financeiro.cash.linkBank.mutationOptions({
			onSuccess: async (_, input) => {
				await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });
				toast.success(
					input.bank_transaction_id === null ? "Depósito desvinculado" : "Depósito vinculado",
				);
				onClose();
			},
			onError: () => toast.error("Não foi possível alterar o vínculo"),
		}),
	);
	const close = () => {
		if (!isPending) {
			onClose();
		}
	};

	return (
		<OverlayShell
			label="Vincular depósito"
			onClose={close}
			className="items-center justify-center p-4"
		>
			<div className="overlay-panel max-w-lg overflow-hidden">
				<div className="border-b border-border p-5">
					<h2 className="type-section-title text-foreground">Vincular depósito</h2>
					<p className="mt-1 text-xs text-muted-foreground">
						Entradas bancárias compatíveis com a saída do caixa.
					</p>
				</div>

				<div className="min-h-40 overflow-y-auto p-3">
					{candidates.isLoading && (
						<div className="flex h-40 items-center justify-center text-muted-foreground">
							<Loader2 className="animate-spin" />
						</div>
					)}
					{candidates.isError && (
						<EmptyState icon={Landmark} title="Não foi possível buscar os depósitos" />
					)}
					{!candidates.isLoading && !candidates.isError && candidates.data?.data.length === 0 && (
						<EmptyState
							icon={ReceiptText}
							title="Nenhum depósito compatível"
							description="Nenhuma entrada tem o mesmo valor em até quatro dias do depósito."
						/>
					)}
					{candidates.data?.data.map((bank) => (
						<div
							key={bank.id}
							className="flex items-center gap-3 rounded-lg px-2 py-3 hover:bg-secondary/50"
						>
							<div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
								<Landmark className="size-4" />
							</div>
							<div className="min-w-0 flex-1">
								<p className="truncate text-xs font-semibold text-foreground">
									{bank.description ?? "Entrada bancária"}
								</p>
								<p className="text-xs text-muted-foreground">{format.date(bank.effective_on)}</p>
							</div>
							<p className="shrink-0 font-mono text-xs font-semibold text-foreground">
								{FinanceiroLabels.money(bank.amount_cents)}
							</p>
							<Button
								size="sm"
								variant="outline"
								disabled={isPending}
								onClick={() => mutate({ id, bank_transaction_id: bank.id })}
							>
								Vincular
							</Button>
						</div>
					))}
				</div>

				<div className="flex justify-end gap-2 border-t border-border p-3">
					{linked && (
						<Button
							variant="ghost"
							disabled={isPending}
							onClick={() => mutate({ id, bank_transaction_id: null })}
						>
							{isPending && <Loader2 className="animate-spin" />}
							Desvincular
						</Button>
					)}
					<Button variant="ghost" disabled={isPending} onClick={close}>
						Fechar
					</Button>
				</div>
			</div>
		</OverlayShell>
	);
}
